import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import AddTradeDialog from "../components/AddTradeDialog";
import CSVImportDialog from "../components/CSVImportDialog";
import MtStatementImport from "../components/MtStatementImport";
import DateInput from "../components/DateInput";
import { Trash, DownloadSimple, MagnifyingGlass, Lightning } from "@phosphor-icons/react";

export default function Trades() {
  const { activeId, activeAccount } = useAccounts();
  const [trades, setTrades] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [mtOpen, setMtOpen] = useState(false);
  const [filters, setFilters] = useState({ q: "", symbol: "", direction: "all", result: "all", from_date: "", to_date: "" });

  const load = useCallback(async () => {
    if (!activeId) return;
    const params = new URLSearchParams({ account_id: activeId });
    if (filters.q) params.set("q", filters.q);
    if (filters.symbol) params.set("symbol", filters.symbol);
    if (filters.direction && filters.direction !== "all") params.set("direction", filters.direction);
    if (filters.result && filters.result !== "all") params.set("result", filters.result);
    if (filters.from_date) params.set("from_date", filters.from_date);
    if (filters.to_date) params.set("to_date", filters.to_date);
    const { data } = await api.get(`/trades?${params.toString()}`);
    setTrades(data);
  }, [activeId, filters]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this trade?")) return;
    await api.delete(`/trades/${id}`);
    toast.success("Deleted");
    load();
  };

  const delAll = async () => {
    if (!window.confirm(`Delete ALL ${trades.length} trades for this account? This cannot be undone.`)) return;
    if (!window.confirm("Are you absolutely sure? This will erase every trade for the active account.")) return;
    try {
      const { data } = await api.delete(`/trades/all?account_id=${activeId}`);
      toast.success(`Deleted ${data.deleted} trades`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const exportPdf = async () => {
    try {
      const token = localStorage.getItem("tj_token");
      const url = `${API}/trades/export/pdf?account_id=${activeId}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.status === 402) return toast.error("PDF export is a paid feature", { description: "Subscribe to unlock" });
      if (!resp.ok) return toast.error("Export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "tradelog-pnl.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF downloaded");
    } catch { toast.error("Export failed"); }
  };

  const reset = () => setFilters({ q: "", symbol: "", direction: "all", result: "all", from_date: "", to_date: "" });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Journal</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Trades</h1>
          <p className="text-sm text-muted-foreground mt-1">{trades.length} entries</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-btn">
            <DownloadSimple size={14} className="mr-1" /> Export PDF
          </Button>
          <Button variant="outline" onClick={() => setMtOpen(true)} data-testid="import-mt-btn">
            <Lightning size={14} weight="fill" className="mr-1" /> Import MT4/MT5
          </Button>
          <Button variant="outline" onClick={() => setCsvOpen(true)} data-testid="import-csv-btn">Import CSV</Button>
          <Button variant="outline" onClick={delAll} disabled={trades.length === 0}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
            data-testid="delete-all-btn">
            <Trash size={14} className="mr-1" /> Delete all
          </Button>
          <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-trade-btn">+ Add Trade</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search notes / symbol…" className="pl-9"
              value={filters.q} onChange={e => setFilters({...filters, q: e.target.value})} data-testid="filter-q" />
          </div>
          <Input placeholder="Symbol" value={filters.symbol}
            onChange={e => setFilters({...filters, symbol: e.target.value})} data-testid="filter-symbol" />
          <Select value={filters.direction} onValueChange={v => setFilters({...filters, direction: v})}>
            <SelectTrigger data-testid="filter-direction"><SelectValue placeholder="Side" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sides</SelectItem>
              <SelectItem value="long">Long</SelectItem>
              <SelectItem value="short">Short</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.result} onValueChange={v => setFilters({...filters, result: v})}>
            <SelectTrigger data-testid="filter-result"><SelectValue placeholder="Result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All results</SelectItem>
              <SelectItem value="winners">Winners only</SelectItem>
              <SelectItem value="losers">Losers only</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={reset} data-testid="filter-reset">Reset</Button>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <DateInput
            value={filters.from_date}
            onChange={v => setFilters({...filters, from_date: v})}
            placeholder="From date"
            data-testid="filter-from"
          />
          <DateInput
            value={filters.to_date}
            onChange={v => setFilters({...filters, to_date: v})}
            placeholder="To date"
            data-testid="filter-to"
          />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] tracking-[0.2em] uppercase">Symbol</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase">Side</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase">Qty</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase text-right">Entry</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase text-right">Exit</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase text-right">P&L</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase">Tags</TableHead>
              <TableHead className="text-[10px] tracking-[0.2em] uppercase">Date</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {trades.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-16">No trades match your filters.</TableCell></TableRow>
            )}
            {trades.map(t => (
              <TableRow key={t.id} data-testid={`trade-row-${t.id}`} className="border-border hover:bg-secondary/40">
                <TableCell className="font-medium">
                  <Link to={`/trades/${t.id}`} className="hover:underline">{t.symbol}</Link>
                </TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${t.direction === "long" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                    {t.direction.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell className="font-mono-num">{t.quantity}</TableCell>
                <TableCell className="text-right font-mono-num">{t.entry_price}</TableCell>
                <TableCell className="text-right font-mono-num">{t.exit_price}</TableCell>
                <TableCell className={`text-right font-mono-num font-bold ${t.pnl >= 0 ? "text-emerald-400" : "text-red-500"}`}>
                  {moneyFor(activeAccount, t.pnl, { sign: true })}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(t.tags || []).slice(0, 3).map(tg => (
                      <span key={tg} className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{tg}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{(t.entry_time || "").slice(0,10)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => del(t.id)} data-testid={`delete-${t.id}`}>
                    <Trash size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AddTradeDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <CSVImportDialog open={csvOpen} onOpenChange={setCsvOpen} onImported={load} />
      <MtStatementImport open={mtOpen} onOpenChange={setMtOpen} onImported={load} />
    </div>
  );
}
